import { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Mail, Phone, Calendar, Eye, Check, Trash2 } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInquiries = () => {
  const { getAuthHeader } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/inquiries`, {
        headers: getAuthHeader(),
      });
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInquiry = (inquiry) => {
    setSelectedInquiry(inquiry);
    setDialogOpen(true);
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}/status?status=${newStatus}`, null, {
        headers: getAuthHeader(),
      });
      toast.success('Status updated');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-700',
    contacted: 'bg-yellow-100 text-yellow-700',
    resolved: 'bg-green-100 text-green-700',
  };

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-inquiries">
        {/* Header */}
        <div>
          <h1 className="text-lg font-bold text-slate-800">Demandes</h1>
          <p className="text-slate-500 text-xs">Gérer les demandes et leads clients</p>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 hover:bg-transparent bg-slate-50">
                <TableHead className="text-slate-600 text-xs">Client</TableHead>
                <TableHead className="text-slate-600 text-xs">Contact</TableHead>
                <TableHead className="text-slate-600 text-xs">Message</TableHead>
                <TableHead className="text-slate-600 text-xs">Date</TableHead>
                <TableHead className="text-slate-600 text-xs">Statut</TableHead>
                <TableHead className="text-slate-600 text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-6 text-xs">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : inquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-6">
                    <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-50" />
                    <span className="text-xs">Aucune demande</span>
                  </TableCell>
                </TableRow>
              ) : (
                inquiries.map((inquiry) => (
                  <TableRow key={inquiry.id} className="border-slate-100" data-testid={`inquiry-row-${inquiry.id}`}>
                    <TableCell>
                      <p className="font-medium text-slate-800 text-xs">{inquiry.name}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <p className="text-[11px] text-slate-600 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {inquiry.email}
                        </p>
                        <p className="text-[11px] text-slate-500 flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {inquiry.phone}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-slate-700 truncate max-w-xs text-xs">{inquiry.message}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-[11px] text-slate-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(inquiry.created_at)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={inquiry.status} 
                        onValueChange={(v) => handleStatusChange(inquiry.id, v)}
                      >
                        <SelectTrigger className="w-24 h-7 bg-transparent border-slate-200 text-xs" data-testid={`status-select-${inquiry.id}`}>
                          <Badge className={`${statusColors[inquiry.status]} text-[10px]`}>
                            {inquiry.status === 'new' ? 'Nouveau' : inquiry.status === 'contacted' ? 'Contacté' : 'Résolu'}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Nouveau</SelectItem>
                          <SelectItem value="contacted">Contacté</SelectItem>
                          <SelectItem value="resolved">Résolu</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewInquiry(inquiry)}
                        className="text-slate-500 hover:text-slate-800 h-7 w-7"
                        data-testid={`view-inquiry-${inquiry.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* View Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Détails de la demande</DialogTitle>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-slate-500">Nom</p>
                  <p className="font-medium text-sm">{selectedInquiry.name}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500">Statut</p>
                  <Badge className={`${statusColors[selectedInquiry.status]} text-xs`}>
                    {selectedInquiry.status === 'new' ? 'Nouveau' : selectedInquiry.status === 'contacted' ? 'Contacté' : 'Résolu'}
                  </Badge>
                </div>
              </div>
              
              <div>
                <p className="text-[10px] text-slate-500">Courriel</p>
                <a href={`mailto:${selectedInquiry.email}`} className="text-blue-600 hover:underline text-sm">
                  {selectedInquiry.email}
                </a>
              </div>
              
              <div>
                <p className="text-[10px] text-slate-500">Téléphone</p>
                <a href={`tel:${selectedInquiry.phone}`} className="text-blue-600 hover:underline text-sm">
                  {selectedInquiry.phone}
                </a>
              </div>
              
              <div>
                <p className="text-[10px] text-slate-500">Message</p>
                <p className="bg-slate-50 p-3 rounded-lg mt-1 text-sm border border-slate-200">{selectedInquiry.message}</p>
              </div>
              
              <div>
                <p className="text-[10px] text-slate-500">Reçu le</p>
                <p className="text-sm">{formatDate(selectedInquiry.created_at)}</p>
              </div>

              {selectedInquiry.vehicle_id && (
                <div>
                  <p className="text-[10px] text-slate-500">Véhicule associé</p>
                  <p className="font-mono text-xs">{selectedInquiry.vehicle_id}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminInquiries;
